<template>
    <div class="table-button" :style="tableContainerStyle">
        <!-- Settings Menu -->
        <div v-if="configurable" class="menu">
            <v-tooltip top>
                <template v-slot:activator="{ on }">
                    <v-btn v-on="on" text fab dark class="menu-item" @click="menuItemClick('edit')">
                        <v-icon size="16">fal fa-edit</v-icon>
                    </v-btn>
                </template>
                <span>{{ $t('general.edit_text') }}</span>
            </v-tooltip>

            <v-tooltip top>
                <template v-slot:activator="{ on }">
                    <v-btn v-on="on" text fab dark class="menu-item" @click="menuItemClick('shape')">
                        <v-icon size="16">fal fa-shapes</v-icon>
                    </v-btn>
                </template>
                <span>{{ $t('general.shape') }}</span>
            </v-tooltip>

            <v-tooltip top>
                <template v-slot:activator="{ on }">
                    <v-btn v-on="on" text fab dark class="menu-item" @click="menuItemClick('duplicate')">
                        <v-icon size="16">fal fa-clone</v-icon>
                    </v-btn>
                </template>
                <span>{{ $t('general.duplicate') }}</span>
            </v-tooltip>

            <v-menu offset-y bottom>
                <template v-slot:activator="{ on: menu }">
                    <v-tooltip top>
                        <template v-slot:activator="{ on: tooltip }">
                            <v-btn v-on="{ ...tooltip, ...menu }" text fab dark class="menu-item">
                                <v-icon size="16">fal fa-palette</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ $t('general.background_color') }}</span>
                    </v-tooltip>
                </template>

                <v-list class="table-button__select-color">
                    <v-list-item
                        v-for="(color, index) in colors"
                        :key="index"
                        @click="menuItemClick('color', color)"
                    >
                        <v-list-item-title :style="`background: ${color};`" />
                    </v-list-item>
                </v-list>
            </v-menu>

            <v-tooltip top>
                <template v-slot:activator="{ on }">
                    <v-btn v-on="on" text fab dark class="menu-item" @click="menuItemClick('delete')">
                        <v-icon size="16">fal fa-times</v-icon>
                    </v-btn>
                </template>
                <span>{{ $t('general.delete') }}</span>
            </v-tooltip>
        </div>

        <v-badge
            v-if="badge"
            bordered
            class="badge"
            :color="badgeColor"
            :icon="badge"
            overlap
        />

        <!-- Button Element -->
        <v-btn
            class="button-element"
            :style="tableStyle"
            :disabled="disabled"
            @pointerdown="pressStart"
            @pointerup="pressStop"
        >
            <div v-if="mode != 'edit'">
                {{ config.name }}
                <br>
                <div
                    v-if="orders.length"
                    class="price"
                    :style="{
                        'background-color': config ? config.color + ' !important' : null
                    }"
                >
                    {{ price.toFormat() }}
                </div>
            </div>
            <input
                v-else
                ref="editNameField"
                v-model="config.name"
                class="edit-name"
                type="text"
                @keyup.enter="finishEdit"
            ></input>
        </v-btn>
    </div>
</template>

<script>
export default {
    props: {
        table: {
            type: Object,
            default: () => {}
        },
        tablePermissions: {
            type: Object,
            default: () => {}
        },
        configurable: {
            type: Boolean,
            default: false
        },
        parentHeight: {
            type: Number,
            default: null
        },
        parentWidth: {
            type: Number,
            default: null
        },
        orders: {
            type: Array,
            default: () => []
        },
        disabled: {
            type: Boolean,
            default: false
        },
        badge: {
            type: String,
            default: null
        },
        badgeColor: {
            type: String,
            default: 'error'
        },
    },

    mounted() {},

    data() {
        return {
            config: this.table,
            mode: null,
            pressTimer: null,
            colors: [ '#82b1ff', '#8f9398', '#f56c6c', '#67c23a', '#e6a23b', '#ffffff' ]
        };
    },

    computed: {
        tableContainerStyle() {
            return {
                'border-radius': this.config && this.config.shape === 'round' ? '100px' : '3px',
                'left': (this.config && this.parentWidth ? (this.config.pos_left * this.parentWidth) / 100 : 0) + 'px',
                'top': (this.config && this.parentHeight ? (this.config.pos_top * this.parentHeight) / 100 : 0) + 'px',
                'width': (this.config && this.parentWidth ? (this.config.width * this.parentWidth) / 100 : 0) + 'px',
                'height': (this.config && this.parentHeight ? (this.config.height * this.parentHeight) / 100 : 0) + 'px'
            };
        },

        tableStyle() {
            let ownTable = false;
            if(this.orders.length > 0) {
                if(this.tablePermissions[this.table.id]) {
                    ownTable = this.tablePermissions[this.table.id] === this.user.id;
                } else {
                    ownTable = this.user && this.orders.some(order => order.user_id === this.user.id)
                }
            }

            return {
                'border-radius': this.config && this.config.shape === 'round' ? '100px' : '3px',
                'background-color': this.config ? this.config.color + ' !important' : null,
                color: this.config ? (this.config.color === '#ffffff' ? '#606266' : '#ffffff') + ' !important' : null,
                border: this.orders.length ? ('3px solid ' + (ownTable ? '#ce4848' : '#416baf')) : 'none',
                opacity: this.disabled ? '0.4' : '1'
            };
        },

        price() {
            return this.orders.reduce((accumulator, currentValue) => {
                return accumulator.add(currentValue.getToPay())
            }, Money.parse(0))
        }
    },

    methods: {
        menuItemClick(item, data = null) {
            if (item === 'edit') {
                this.mode = 'edit';
                this.$nextTick(() => {
                    this.$refs.editNameField.focus();
                    this.$refs.editNameField.select();
                });
            } else if (item === 'shape') {
                this.config.shape = this.config.shape == 'round' ? 'rectangular' : 'round';
                this.$emit('update', this.config);
            } else if (item === 'duplicate') {
                this.$emit('duplicate', this.config);
            } else if (item === 'color') {
                this.config.color = data;
                this.$emit('update', this.config);
            } else if (item === 'delete') {
                this.$emit('delete', this.config);
            }
        },

        finishEdit() {
            this.mode = null;
            this.$emit('update', this.config);
        },

        pressStart() {
            this.longPress = false
            this.pressTimer = setTimeout(() => {
                this.longPress = true
            }, 1000)
        },

        pressStop() {
            if (this.pressTimer !== null) {
                clearTimeout(this.pressTimer);
                this.pressTimer = null;
            }

            if(this.longPress) {
                this.longPress = false
                this.$emit('long-press', this.config)
                return
            }

            this.$emit('click', this.config)
        }
    }
};
</script>

<style lang="scss">
.table-button {
    position: absolute;

    .badge {
        position: absolute;
        z-index: 1;
        right: 0;
    }

    &.draggable {
        .button-element {
            cursor: inherit;
        }
    }

    &:hover {
        .menu {
            visibility: visible;
            opacity: 1;
        }
    }

    .menu {
        visibility: hidden;
        opacity: 0;
        transition: visibility 0s, opacity 0.2s linear;
        background: #2f404f;
        border-radius: 3px;
        padding: 4px;
        position: absolute;
        bottom: -18px;
        left: -20px;
        right: -20px;
        margin: 0 auto;
        width: 135px;
        cursor: pointer;
        z-index: 10000;

        .menu-item {
            margin: 0px;
            height: 25px;
            width: 25px;
            min-width: 0;

            &:hover {
                color: #ffd04b;
            }
        }
    }

    .button-element {
        border: none;
        height: 100% !important;
        width: 100%;
        min-width: 0 !important;
        color: #303133;
        margin: 0;
        text-transform: none;
        white-space: normal;
        word-break: break-word;

        &:hover {
            color: inherit;
        }

        .price {
            font-weight: bold;
            border-radius: 4px;
            padding: 2px;
        }
    }

    .edit-name {
        background: none;
        border: none;
        text-align: center;
        outline: none;
        border-bottom: 1px solid gray;
        width: 100%;
    }
}

.table-button__select-color {
    border: none;
    width: 100px;
    margin: 0;
    padding: 0 !important;

    & * {
        padding: 0 !important;
        margin: 0 !important;
        height: 30px !important;
    }

    .v-list-item {
        min-height: 0;

        &:hover {
            opacity: 0.9;
        }
    }
}
</style>
